/**
 * FERP Portal - Record Detail Drawer (DRY)
 *
 * Reusable drawer that displays any record's fields as a key-value table.
 * Used by TabulatorPrimitiveManager, WorkflowStateView, and any card/row
 * that needs "click to see detail" behavior.
 *
 * Features:
 * - Expand/collapse toggle (lg ↔ xl)
 * - Optional "Open in View" link to navigate to primitive list page
 *
 * Usage:
 *   const drawer = useRecordDrawer();
 *   <Card className="selectable-card" onClick={() => drawer.open(record)}>
 *   <RecordDetailDrawer {...drawer} viewUrl="/ferp-core/tasks" />
 */

import { useState, useCallback } from 'react';
import {
  Drawer, Text, Badge, Table, ScrollArea, Stack, Group, ActionIcon, Tooltip, Button,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowsMaximize, IconArrowsMinimize, IconExternalLink } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useResolveActorName, ACTOR_ID_FIELDS } from '@/contexts/ActorNameContext';
import { useResolveRulesetName } from '@/hooks/useResolveRulesetName';

// ============================================================================
// FIELD FORMATTING (extracted from TabulatorPrimitiveManager)
// ============================================================================

/** Convert camelCase field name to human-readable label */
export function formatFieldName(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').trim();
}

/**
 * Internal ID fields hidden from default detail views (DRR-124).
 * These expose tenant_num, sequential ordering, or internal indexing keys.
 * Visible only to platform architects via RequireScope("platform:debug").
 *
 * Actor ID fields (assignedActorId, subjectActorId, etc.) are listed here
 * because they're resolved via ACTOR_ID_FIELDS + FieldValue auto-resolution.
 * Hiding prevents showing raw sequential IDs when resolution fails.
 *
 * Audit: verified against generated/graphql.ts — every field here exists.
 */
export const INTERNAL_ID_FIELDS: ReadonlySet<string> = new Set([
  // ── Identity / tenant ───────────────────────────────────────────────
  'id',
  'tenantId',
  'actorGlobalId',
  'authUserId',

  // ── Actor ID references (resolved via ACTOR_ID_FIELDS, hidden as fallback) ──
  'assignedActorId',
  'subjectActorId',
  'createdBy',
  'createdByUserId',
  'auditorActorId',
  'authorityActorId',
  'complianceOfficerId',
  'ownerActorId',
  'supervisedByActorId',
  'recoveredByActorId',
  'fromActorId',
  'toActorId',
  'senderActorId',
  'authorActorId',
  'subjectActorId',
  'genesisActorId',

  // ── Cross-entity single ID references ───────────────────────────────
  'parentId',
  'workflowInstanceId',
  'compensationTaskId',
  'entityId',
  'taskId',
  'policyId',
  'templateId',
  'workflowId',
  'contractId',
  'queueId',
  'slaId',
  'approvalChainId',
  'approvalWorkflowId',
  'checkpointId',
  'correlationId',
  'constellationId',
  'parallelGroupId',
  'externalId',

  // ── Array ID references (enumeration risk) ──────────────────────────
  'notificationActorIds',
  'approverActorIds',
  'eligibleActorIds',
  'recipientActorIds',
  'allowedActorIds',
  'actorPoolIds',
  'constraintIds',
  'validationIds',
  'workflowInstanceIds',
  'checkpointIds',
  'eventIds',
  'compensationTaskIds',
  'notificationIds',
  'safetyChecklistIds',
  'participantTenantIds',
  'queueIds',
]);

/** Image URL pattern for auto-detecting image field values */
const IMAGE_URL_RE = /\.(jpg|jpeg|png|gif|webp)(\?|$)/i;

/**
 * Render a field value with appropriate formatting.
 *
 * When `fieldKey` is provided, auto-resolves actor IDs and constellation IDs
 * via ActorNameContext (DRY — no per-component resolver wiring needed).
 */
/** Fields that contain ruleset/mandate IDs needing resolution */
const MANDATE_ID_FIELDS: ReadonlySet<string> = new Set([
  'mandateRef',
  'policyRef',
  'rulesetRef',
]);

export function FieldValue({ value, fieldKey, entity }: {
  value: unknown;
  fieldKey?: string;
  entity?: Record<string, unknown>;
}) {
  const resolveActor = useResolveActorName();
  const resolveRuleset = useResolveRulesetName();

  if (value === null || value === undefined) {
    return <Text c="dimmed" size="sm">—</Text>;
  }

  // Auto-resolve actor IDs, constellation IDs, and mandate refs when fieldKey is provided
  if (fieldKey && typeof value === 'string') {
    // Actor ID resolution (DRR-124)
    if (ACTOR_ID_FIELDS.has(fieldKey)) {
      const name = resolveActor(value);
      if (name !== value) {
        return <Tooltip label={value}><Text size="sm">{name}</Text></Tooltip>;
      }
    }
    // Constellation ID resolution
    if (fieldKey === 'constellationId' && entity?.subjectActorId) {
      const name = resolveActor(entity.subjectActorId as string);
      if (name !== String(entity.subjectActorId)) {
        return <Tooltip label={String(value)}><Text size="sm">{name}</Text></Tooltip>;
      }
    }
    // Mandate/ruleset ID resolution (DRR-117)
    if (MANDATE_ID_FIELDS.has(fieldKey)) {
      const name = resolveRuleset(value);
      if (name !== value) {
        return <Tooltip label={value}><Text size="sm">{name}</Text></Tooltip>;
      }
    }
  }

  if (typeof value === 'boolean') {
    return <Badge size="sm" c={value ? 'green' : 'gray'}>{value ? 'Yes' : 'No'}</Badge>;
  }
  // Detect image URL → render thumbnail
  if (typeof value === 'string' && IMAGE_URL_RE.test(value)) {
    return (
      <img
        src={value}
        alt=""
        style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
        loading="lazy"
      />
    );
  }
  if (typeof value === 'object') {
    return (
      <Text size="sm" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
        {JSON.stringify(value, null, 2)}
      </Text>
    );
  }
  return <Text size="sm">{String(value)}</Text>;
}

// ============================================================================
// HOOK
// ============================================================================

export interface UseRecordDrawerReturn<T extends Record<string, unknown>> {
  selectedRecord: T | null;
  opened: boolean;
  open: (record: T) => void;
  close: () => void;
}

/** State hook for record detail drawer. Pairs with <RecordDetailDrawer />. */
export function useRecordDrawer<T extends Record<string, unknown>>(): UseRecordDrawerReturn<T> {
  const [opened, { open: openDrawer, close }] = useDisclosure(false);
  const [selectedRecord, setSelectedRecord] = useState<T | null>(null);

  const open = useCallback((record: T) => {
    setSelectedRecord(record);
    openDrawer();
  }, [openDrawer]);

  return { selectedRecord, opened, open, close };
}

// ============================================================================
// COMPONENT
// ============================================================================

interface RecordDetailDrawerProps<T extends Record<string, unknown>> {
  selectedRecord: T | null;
  opened: boolean;
  close: () => void;
  /** Override the drawer title (defaults to record's `name` field or "Record Detail") */
  title?: string;
  /** Fields to exclude from the detail table */
  excludeFields?: string[];
  /** Optional status badge color mapping */
  statusColor?: string;
  /** URL to navigate to when "Open in View" is clicked (e.g. "/ferp-core/tasks") */
  viewUrl?: string | ((record: T) => string);
  /** Label for the view link button */
  viewLabel?: string;
  /** Additional content rendered below the field table */
  children?: React.ReactNode;
}

/** Reusable drawer showing any record's fields as a key-value table. */
export function RecordDetailDrawer<T extends Record<string, unknown>>({
  selectedRecord,
  opened,
  close,
  title,
  excludeFields = ['__typename'],
  statusColor,
  viewUrl,
  viewLabel = 'Open in View',
  children,
}: RecordDetailDrawerProps<T>) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const recordName = selectedRecord?.name as string | undefined;
  const recordStatus = selectedRecord?.status as string | undefined;

  const resolvedViewUrl = selectedRecord && viewUrl
    ? typeof viewUrl === 'function' ? viewUrl(selectedRecord) : viewUrl
    : null;

  const handleOpenInView = () => {
    if (resolvedViewUrl) {
      close();
      navigate(resolvedViewUrl);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={() => { close(); setExpanded(false); }}
      position="right"
      size={expanded ? 'xl' : 'lg'}
      zIndex={1100}
      title={
        <Group gap="xs">
          <Text fw={600}>{title ?? recordName ?? 'Record Detail'}</Text>
          {recordStatus && (
            <Badge size="sm" variant="light" c={statusColor ?? 'gray'}>
              {recordStatus}
            </Badge>
          )}
        </Group>
      }
    >
      {/* Toolbar: expand toggle + open-in-view link */}
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          {resolvedViewUrl && (
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconExternalLink size={14} />}
              onClick={handleOpenInView}
            >
              {viewLabel}
            </Button>
          )}
        </Group>
        <Tooltip label={expanded ? 'Collapse' : 'Expand'}>
          <ActionIcon variant="subtle" size="sm" onClick={() => setExpanded(e => !e)}>
            {expanded ? <IconArrowsMinimize size={16} /> : <IconArrowsMaximize size={16} />}
          </ActionIcon>
        </Tooltip>
      </Group>

      <ScrollArea h="calc(100vh - 160px)">
        <Stack gap="sm">
          {selectedRecord && (
            <Table striped highlightOnHover withTableBorder>
              <Table.Tbody>
                {Object.entries(selectedRecord)
                  .filter(([key]) => !key.startsWith('_') && !excludeFields.includes(key) && !INTERNAL_ID_FIELDS.has(key))
                  .map(([key, value]) => (
                    <Table.Tr key={key}>
                      <Table.Td fw={600} w={140} style={{ textTransform: 'capitalize' }}>
                        {formatFieldName(key)}
                      </Table.Td>
                      <Table.Td>
                        <FieldValue value={value} fieldKey={key} entity={selectedRecord as Record<string, unknown>} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          )}
          {children}
        </Stack>
      </ScrollArea>
    </Drawer>
  );
}
